import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { doc, getDoc } from 'firebase/firestore';
import db, { functions } from '../../firebaseConfig';
import usePageTitle from '../../hooks/usePageTitle';
import {
  BIRTH_TIME_OPTIONS,
  buildDefaultServiceAnswerPlans,
  canRemoveServiceAnswerPlan,
  getServiceAnswerPlanSameAsSource,
  getServiceAnswerPlanProgress,
  getVisibleServiceQuestions,
  isAnswerGroupCountAdjustEnabled,
  labelServiceAnswerPlan,
  normalizeServiceAnswerPlansForGroups,
  sanitizeVisibleServiceResponses,
  serviceAnswerIsFilled,
  validateServiceQuestionAnswer,
} from '../../utils/serviceQuestionnaire';
import {
  getServiceTaskStage,
  isPendingServiceTask,
  isSubmittedServiceTask,
} from '../../utils/serviceTaskStatus';
import { DEFAULT_SHOP_USER_ID, getScopedSettingsDocId } from '../../utils/tianlanStore';
import {
  DEFAULT_TIANLAN_CUSTOMER_THEME,
  normalizeTianlanCustomerTheme,
} from '../../utils/tianlanCustomerTheme';
import NativeBirthTimeSelect from './NativeBirthTimeSelect';
import TianlanSecureLinkLoading from './TianlanSecureLinkLoading';
import '../../styles.css';
import '../../tianlan-styles.css';

const CUSTOMER_LOGO_URL = 'https://cms-static.cdn.91app.com/images/original/11/20907f91-ac0f-46b4-82b1-0aebda39ccbc-1632897043-6ggal54opq_d.png';
const LINE_ICON_URL = 'https://cdn.simpleicons.org/line/06C755';
const CUSTOMER_SUPPORT_URL = 'https://r.botbonnie.com/kOSEn';
const DEFAULT_ANNOUNCEMENT_STYLE = {
  backgroundColor: '#f7c873',
  textColor: '#2c3e50',
};
const DEFAULT_CUSTOMER_THEME = DEFAULT_TIANLAN_CUSTOMER_THEME;
const getProductKey = (product) => String(product?.id || product?.commodityId || product?.jamboCommodityId || '');
const normalizeStyleKey = (value) => String(value || '').trim().replace(/\s+/g, '').replace(/[／/\\-]/g, '').toLowerCase() || 'noStyle';
const legacyStyleKey = (value) => String(value || '').trim().replace(/\s+/g, '').toLowerCase() || 'noStyle';
const normalizeText = (value) => String(value || '').trim();
const isDisplayableStyle = (style) => {
  const text = normalizeText(style);
  if (!text) return false;
  const key = normalizeStyleKey(text);
  return !['noStyle', 'nostyle', '無樣式', '默認', '預設', '請選擇'].includes(key);
};
const CUSTOMER_DRAFT_TTL_MS = 24 * 60 * 60 * 1000;
const customerDraftKeyFor = (results = [], serviceTasks = []) => {
  const keys = [...new Set([
    ...results.map((item) => `${item.shopUserId || item.storeId || ''}_${item.paNumber || ''}`),
    ...serviceTasks.map((item) => `${item.shopUserId || item.storeId || ''}_${item.paNumber || ''}`),
  ].filter((key) => !key.endsWith('_')))];
  return keys.length ? `tianlan_customer_draft_${keys.sort().join('__')}` : '';
};

const stockSettingByStyle = (collection = {}, style = '') => {
  const normalizedKey = normalizeStyleKey(style);
  const oldKey = legacyStyleKey(style);
  if (collection[style]) return collection[style];
  if (collection[style?.trim?.()]) return collection[style.trim()];
  if (collection[normalizedKey]) return collection[normalizedKey];
  if (collection[oldKey]) return collection[oldKey];
  const matchedKey = Object.keys(collection).find((key) => (
    normalizeStyleKey(key) === normalizedKey ||
    legacyStyleKey(key) === oldKey ||
    normalizeStyleKey(collection[key]?.styleKey) === normalizedKey
  ));
  return matchedKey ? collection[matchedKey] : null;
};

const stockInfoForStyle = (product, style = '') => (
  stockSettingByStyle(product?.stockData, style) ||
  stockSettingByStyle(product?.manualStockByStyle, style) ||
  stockSettingByStyle(product?.stockSettings, style) ||
  null
);

const stockInfoIsAvailable = (stockInfo) => {
  if (!stockInfo) return false;
  const mode = stockInfo.mode || stockInfo.manualStockMode;
  if (mode === 'unlimited' || stockInfo.unlimited === true) return true;
  return Number(stockInfo.remainingQuantity ?? stockInfo.availableQuantity ?? stockInfo.quantity ?? stockInfo.manualStockQuantity ?? 0) > 0;
};

const stockInfoQuantity = (stockInfo) => {
  if (!stockInfo) return 0;
  const mode = stockInfo.mode || stockInfo.manualStockMode;
  if (mode === 'unlimited' || stockInfo.unlimited === true) return 999999;
  return Number(stockInfo.remainingQuantity ?? stockInfo.availableQuantity ?? stockInfo.quantity ?? stockInfo.manualStockQuantity ?? 0) || 0;
};

const getDisplayText = () => ({
  originalItemLabel: '缺貨商品',
  exchangeItemLabel: '換貨商品',
  arrowText: '換貨',
  actionLabel: '換貨方案',
});

const getSelectionSummary = (selection) => {
  if (!selection) return '尚未選擇';
  if (selection.action === 'cancel') return '不換貨，申請退款';
  return [selection.productName, isDisplayableStyle(selection.style) ? selection.style : ''].filter(Boolean).join(' / ') || '已選擇換貨商品';
};

const styleChoicesForProduct = (product) => (
  (product?.styles || []).filter(isDisplayableStyle)
);

const isNoStyleProduct = (product) => (
  !product?.styles ||
  product.styles.length === 0 ||
  (product.styles.length === 1 && (!product.styles[0] || product.styles[0].trim() === ''))
);

const availableStyleChoicesForProduct = (product) => {
  const styles = styleChoicesForProduct(product);
  if (styles.length === 0) return [];
  const stockInfo = getStockInfo(product, false);
  return styles.filter((style) => stockInfo.availableStyles?.includes(style));
};

const productNeedsStyleChoice = (product) => availableStyleChoicesForProduct(product).length > 1;

const isCompletedExchangeItem = (item) => {
  if (!item) return false;
  if (item.customerSelection?.action) return true;
  if (['filled', 'exchanged', 'handled', 'refund_requested', 'out_of_stock'].includes(item.status)) return true;
  if (['success', 'handled'].includes(item.jamboExchangeStatus)) return true;
  if (['success', 'handled'].includes(item.jobStatus)) return true;
  if (item.serviceStatus === 'handled') return true;
  return false;
};

const isExpiredExchangeItem = (item) => {
  if (!item?.exchangeDeadline || item.customerSelection?.action) return false;
  const deadlineMs = new Date(item.exchangeDeadline).getTime();
  return Number.isFinite(deadlineMs) && deadlineMs < Date.now();
};

const formatDeadline = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (!Number.isFinite(date.getTime())) return '';
  return date.toLocaleString('zh-TW', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
};

const padDateNumber = (value) => String(value || '').padStart(2, '0');

const parseServiceDateValue = (value) => {
  const raw = String(value || '');
  const [datePart = '', timePart = ''] = raw.split('T');
  const [year = '', month = '', day = ''] = datePart.split('-');
  return {
    year: /^\d{4}$/.test(year) ? year : '',
    month: /^\d{2}$/.test(month) ? month : '',
    day: /^\d{2}$/.test(day) ? day : '',
    time: /^\d{2}:\d{2}/.test(timePart) ? timePart.slice(0, 5) : '',
  };
};

const daysInServiceMonth = (year, month) => {
  const parsedYear = Number(year);
  const parsedMonth = Number(month);
  if (!parsedYear || !parsedMonth) return 31;
  return new Date(parsedYear, parsedMonth, 0).getDate();
};

const buildServiceDateValue = (parts, dateMode = 'date') => {
  const hasDatePart = Boolean(parts.year || parts.month || parts.day);
  if (!hasDatePart && dateMode !== 'datetime') return '';
  if (!hasDatePart && dateMode === 'datetime' && !parts.time) return '';
  const dateText = `${parts.year || ''}-${parts.month || ''}-${parts.day || ''}`;
  if (dateMode === 'datetime') {
    return parts.time ? `${dateText}T${parts.time}` : dateText;
  }
  return dateText;
};

const updateServiceDatePart = (value, field, nextValue, dateMode = 'date') => {
  const parts = parseServiceDateValue(value);
  parts[field] = nextValue;
  if (field === 'year' || field === 'month') {
    const dayLimit = daysInServiceMonth(parts.year, parts.month);
    if (Number(parts.day) > dayLimit) {
      parts.day = padDateNumber(dayLimit);
    }
  }
  return buildServiceDateValue(parts, dateMode);
};

const validateServiceAnswer = validateServiceQuestionAnswer;

const updateCalendarDatePart = (value, field, nextValue) => ({
  calendar: value?.calendar || '',
  year: value?.year || '',
  month: value?.month || '',
  day: value?.day || '',
  [field]: nextValue,
});

const isExpiredServiceTask = (task) => {
  return getServiceTaskStage(task) === 'expired';
};

const serviceTaskStatusText = (task) => {
  const stage = getServiceTaskStage(task);
  if (stage === 'expired') return '已過期';
  if (stage === 'handled') return '已處理';
  if (stage === 'submitted') return '已送出';
  return '待填寫';
};

const resultIdentityOf = (result) => {
  const item = result?.exchangeItem || {};
  return [
    result?.shopUserId,
    item.id,
    item.jamboSplitKey,
    item.jamboOrderItemId || item.orderId,
    item.jamboSplitIndex ?? 0,
  ].filter((value) => value !== undefined && value !== null && value !== '').join('_');
};

const getExchangeItemSelectionKey = (item = {}) => (
  [
    item.id,
    item.jamboSplitKey,
    item.jamboOrderItemId || item.orderId,
    item.jamboSplitIndex ?? 0,
  ].filter((value) => value !== undefined && value !== null && value !== '').join('_') || String(item.id || '')
);

const resultSortValueOf = (result) => {
  const item = result?.exchangeItem || {};
  return [
    item.createdAt || item.paidAt || '',
    String(item.jamboOrderItemId || item.orderId || ''),
    Number(item.jamboSplitIndex || 0),
  ].join('_');
};

const getVisibleExchangeResults = (rawResults) => {
  const byId = new Map();
  (rawResults || []).forEach((result) => {
    if (isCompletedExchangeItem(result.exchangeItem)) return;
    if (isExpiredExchangeItem(result.exchangeItem)) return;
    const key = resultIdentityOf(result);
    if (!key || byId.has(key)) return;
    byId.set(key, result);
  });
  return [...byId.values()].sort((a, b) => (
    resultSortValueOf(a).localeCompare(resultSortValueOf(b), 'zh-Hant', { numeric: true })
  ));
};

const isPaQuery = (value) => /^PA\d+-\d+$/i.test(String(value || '').trim());

const orderKeyOf = (result) => `${result?.shopUserId || ''}_${result?.paNumber || ''}`;
const serviceTaskOrderKeyOf = (task) => `${task?.shopUserId || task?.storeId || ''}_${task?.paNumber || ''}`;

const buildOrderChoices = (items = []) => {
  const map = new Map();
  items.forEach((result) => {
    const key = orderKeyOf(result);
    if (!key.trim() || map.has(key)) {
      if (map.has(key)) map.get(key).items.push(result.exchangeItem);
      return;
    }
    map.set(key, {
      key,
      shopUserId: result.shopUserId,
      storeName: result.storeName,
      paNumber: result.paNumber,
      items: [result.exchangeItem],
      firstItem: result.exchangeItem || {},
    });
  });
  return [...map.values()].sort((a, b) => (
    String(a.paNumber || '').localeCompare(String(b.paNumber || ''), 'zh-Hant', { numeric: true })
  ));
};

const buildCombinedOrderChoices = (exchangeResults = [], taskList = []) => {
  const byKey = new Map(
    buildOrderChoices(exchangeResults).map((choice) => [
      choice.key,
      {
        ...choice,
        serviceTasks: [],
      },
    ]),
  );
  (taskList || []).forEach((task) => {
    const key = serviceTaskOrderKeyOf(task);
    if (!key.trim()) return;
    const current = byKey.get(key) || {
      key,
      shopUserId: task.shopUserId || task.storeId,
      storeName: task.storeName,
      paNumber: task.paNumber,
      items: [],
      serviceTasks: [],
      firstItem: task,
    };
    current.serviceTasks = current.serviceTasks || [];
    current.serviceTasks.push(task);
    byKey.set(key, current);
  });
  return [...byKey.values()].sort((a, b) => (
    String(a.paNumber || '').localeCompare(String(b.paNumber || ''), 'zh-Hant', { numeric: true })
  ));
};

const orderChoiceHasPendingWork = (choice = {}) => {
  const hasExchangeItems = (choice.items || []).some((item) => (
    !isCompletedExchangeItem(item) && !isExpiredExchangeItem(item)
  ));
  const hasServiceTasks = (choice.serviceTasks || []).some((task) => (
    isPendingServiceTask(task) && !isExpiredServiceTask(task)
  ));
  return hasExchangeItems || hasServiceTasks;
};

const serviceFormGroupKeyOf = (task = {}) => [
  task.serviceGroupKey || '',
  task.shopUserId || task.storeId || '',
  task.paNumber || '',
  task.questionTemplateId || task.formId || '',
  task.jamboCommodityId || task.product?.commodityId || task.product?.id || '',
  normalizeStyleKey(task.originalStyle || task.style || ''),
].join('@@');

const groupServiceFormTasks = (tasks = []) => {
  const groups = new Map();
  tasks.forEach((task) => {
    const key = serviceFormGroupKeyOf(task);
    const current = groups.get(key) || { key, tasks: [] };
    current.tasks.push(task);
    groups.set(key, current);
  });
  return [...groups.values()];
};

const makeAnswerPlanId = () => (
  window.crypto?.randomUUID
    ? `answer_${window.crypto.randomUUID()}`
    : `answer_${Date.now()}_${Math.random().toString(16).slice(2)}`
);

const sameAsLinkKey = (planId, questionId) => `${planId}@@${questionId}`;
const cloneServiceAnswer = (value) => (
  value && typeof value === 'object' ? JSON.parse(JSON.stringify(value)) : value
);

function TianlanJamboCustomerQuery() {
  usePageTitle('訂單售後服務平台', '天藍小舖', '跨商店查詢售後服務任務');
  const [search, setSearch] = useState(() => new URLSearchParams(window.location.search).get('q') || '');
  const [accessLinkToken, setAccessLinkToken] = useState(() => new URLSearchParams(window.location.search).get('t') || '');
  const [customerAccessToken, setCustomerAccessToken] = useState('');
  const [draftKey, setDraftKey] = useState('');
  const [results, setResults] = useState([]);
  const [serviceTasks, setServiceTasks] = useState([]);
  const [allResults, setAllResults] = useState([]);
  const [allServiceTasks, setAllServiceTasks] = useState([]);
  const [serviceTemplates, setServiceTemplates] = useState({});
  const [serviceAnswers, setServiceAnswers] = useState({});
  const [serviceErrors, setServiceErrors] = useState({});
  const [serviceAnswerPlans, setServiceAnswerPlans] = useState({});
  const [serviceSameAsLinks, setServiceSameAsLinks] = useState({});
  const [collapsedServiceAnswers, setCollapsedServiceAnswers] = useState({});
  const [selectedMap, setSelectedMap] = useState({});
  const [isLoading, setIsLoading] = useState(() => Boolean(accessLinkToken));
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [viewMode, setViewMode] = useState('search');
  const [notice, setNotice] = useState(null);
  const [announcement, setAnnouncement] = useState({
    content: '',
    style: DEFAULT_ANNOUNCEMENT_STYLE,
    shopUserId: DEFAULT_SHOP_USER_ID,
  });
  const [customerTheme, setCustomerTheme] = useState(DEFAULT_CUSTOMER_THEME);

  const showNotice = (nextNotice) => {
    setNotice({
      tone: 'info',
      actionLabel: '我知道了',
      ...nextNotice,
    });
  };

  const loadAnnouncement = useCallback(async (shopUserId = DEFAULT_SHOP_USER_ID) => {
    const scopedShop = { shopUserId };
    try {
      const [announcementDoc, styleDoc, customerThemeDoc] = await Promise.all([
        getDoc(doc(db, 'tianlan_settings', getScopedSettingsDocId('announcement', scopedShop))),
        getDoc(doc(db, 'tianlan_settings', getScopedSettingsDocId('announcement_style', scopedShop))),
        getDoc(doc(db, 'tianlan_settings', getScopedSettingsDocId('customer_theme', scopedShop))),
      ]);

      if (!announcementDoc.exists() && shopUserId !== DEFAULT_SHOP_USER_ID) {
        await loadAnnouncement(DEFAULT_SHOP_USER_ID);
        return;
      }

      setAnnouncement({
        content: String(announcementDoc.data()?.content || '').trim(),
        style: {
          ...DEFAULT_ANNOUNCEMENT_STYLE,
          ...(styleDoc.exists() ? styleDoc.data() : {}),
        },
        shopUserId,
      });
      setCustomerTheme(normalizeTianlanCustomerTheme(
        customerThemeDoc.exists() ? customerThemeDoc.data() : {},
      ));
    } catch (error) {
      console.warn('load customer announcement failed:', error);
    }
  }, []);

  useEffect(() => {
    loadAnnouncement(DEFAULT_SHOP_USER_ID);
  }, [loadAnnouncement]);

  const grouped = useMemo(() => {
    const map = {};
    results.forEach((result) => {
      const key = `${result.shopUserId}_${result.paNumber}`;
      if (!map[key]) {
        map[key] = {
          key,
          shopUserId: result.shopUserId,
          storeName: result.storeName,
          paNumber: result.paNumber,
          items: [],
        };
      }
      map[key].items.push(result.exchangeItem);
    });
    serviceTasks.forEach((task) => {
      const key = `${task.shopUserId || task.storeId}_${task.paNumber}`;
      if (!map[key]) {
        map[key] = {
          key,
          shopUserId: task.shopUserId || task.storeId,
          storeName: task.storeName,
          paNumber: task.paNumber,
          items: [],
          serviceTasks: [],
        };
      }
      map[key].serviceTasks = map[key].serviceTasks || [];
      map[key].serviceTasks.push(task);
    });
    return Object.values(map);
  }, [results, serviceTasks]);
  const currentServiceFormGroups = useMemo(
    () => groupServiceFormTasks(serviceTasks.filter((task) => isPendingServiceTask(task) && !isExpiredServiceTask(task))),
    [serviceTasks],
  );
  const serviceTemplateForGroup = useCallback((serviceGroup) => {
    const firstTask = serviceGroup?.tasks?.[0] || {};
    return serviceTemplates[firstTask.questionTemplateId || firstTask.formId || ''] || {};
  }, [serviceTemplates]);
  const defaultAnswerPlansFor = useCallback((serviceGroup) => (
    buildDefaultServiceAnswerPlans(serviceGroup, serviceTemplateForGroup(serviceGroup))
  ), [serviceTemplateForGroup]);

  useEffect(() => {
    setServiceAnswerPlans((previous) => normalizeServiceAnswerPlansForGroups(
      currentServiceFormGroups,
      serviceTemplates,
      previous,
    ));
  }, [currentServiceFormGroups, serviceTemplates]);
  const orderChoices = useMemo(() => buildCombinedOrderChoices(results, serviceTasks), [results, serviceTasks]);
  const doSearch = async ({ silentEmptyNotice = false, lookupValue = search, linkToken = '' } = {}) => {
    const normalizedLookup = String(lookupValue || '').trim();
    const effectiveLinkToken = linkToken || accessLinkToken;
    if (!normalizedLookup && !effectiveLinkToken) {
      showNotice({
        tone: 'warning',
        title: '請先輸入查詢資料',
        message: '請輸入付款單 PA 單號或收件人手機，系統才可以查詢售後服務項目。',
      });
      return;
    }
    setIsLoading(true);
    try {
      const resolve = httpsCallable(functions, 'tianlanResolveCustomerQuery');
      const response = await resolve(effectiveLinkToken
        ? { accessLinkToken: effectiveLinkToken }
        : { search: normalizedLookup });
      const rawResults = response.data?.results || [];
      const matchedServiceTasks = response.data?.serviceTasks || [];
      const nextResults = getVisibleExchangeResults(rawResults);
      const nextServiceTasks = matchedServiceTasks.filter((task) => (
        isPendingServiceTask(task) && !isExpiredServiceTask(task)
      ));
      const expiredResults = rawResults.filter((result) => isExpiredExchangeItem(result.exchangeItem));
      const expiredServiceTasks = matchedServiceTasks.filter(isExpiredServiceTask);
      const resultShopUserId = nextResults[0]?.shopUserId || rawResults[0]?.shopUserId;
      if (resultShopUserId && resultShopUserId !== announcement.shopUserId) {
        loadAnnouncement(resultShopUserId);
      }
      setResults(nextResults);
      setServiceTasks(nextServiceTasks);
      setAllResults(nextResults);
      setAllServiceTasks(nextServiceTasks);
      const nextServiceTemplates = response.data?.serviceTemplates || {};
      setServiceTemplates(nextServiceTemplates);
      setCustomerAccessToken(response.data?.customerAccessToken || '');
      setServiceAnswers({});
      setServiceAnswerPlans({});
      setServiceSameAsLinks({});
      setCollapsedServiceAnswers({});
      setServiceErrors({});
      setSelectedMap({});
      const nextDraftKey = customerDraftKeyFor(nextResults, nextServiceTasks);
      setDraftKey(nextDraftKey);
      if (nextDraftKey) {
        try {
          const savedDraft = JSON.parse(window.localStorage.getItem(nextDraftKey) || 'null');
          if (savedDraft?.savedAt && Date.now() - savedDraft.savedAt < CUSTOMER_DRAFT_TTL_MS) {
            setServiceAnswers(savedDraft.serviceAnswers || {});
            const draftServiceGroups = groupServiceFormTasks(
              nextServiceTasks.filter((task) => isPendingServiceTask(task) && !isExpiredServiceTask(task)),
            );
            setServiceAnswerPlans(normalizeServiceAnswerPlansForGroups(
              draftServiceGroups,
              nextServiceTemplates,
              savedDraft.serviceAnswerPlans || {},
            ));
            setServiceSameAsLinks(savedDraft.serviceSameAsLinks || {});
            setCollapsedServiceAnswers(savedDraft.collapsedServiceAnswers || {});
            setSelectedMap(savedDraft.selectedMap || {});
          } else if (savedDraft) {
            window.localStorage.removeItem(nextDraftKey);
          }
        } catch (_) {
          window.localStorage.removeItem(nextDraftKey);
        }
      }
      if (nextResults.length > 0 || nextServiceTasks.length > 0) {
        const choices = buildCombinedOrderChoices(nextResults, nextServiceTasks);
        setViewMode(choices.length > 1 && (effectiveLinkToken || !isPaQuery(normalizedLookup)) ? 'orderSelect' : 'exchange');
        window.setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 0);
      } else if (!silentEmptyNotice && response.data?.hasSubmittedForms) {
        if (effectiveLinkToken) backToSearch();
        showNotice({
          tone: 'success',
          title: '此訂單的資料已送出',
          message: '我們已收到您填寫的資料，目前無需再次填寫。如需修改，請聯絡客服協助。',
          actionLabel: '回首頁',
          onAction: () => { setNotice(null); backToSearch(); },
          secondaryActionLabel: '聯絡客服',
          onSecondaryAction: () => window.open(CUSTOMER_SUPPORT_URL, '_blank', 'noopener,noreferrer'),
        });
      } else if (!silentEmptyNotice && (rawResults.length > 0 || matchedServiceTasks.length > 0)) {
        if (effectiveLinkToken) backToSearch();
        if (expiredResults.length > 0 || expiredServiceTasks.length > 0) {
          const deadline =
            expiredResults.map((result) => formatDeadline(result.exchangeItem?.exchangeDeadline)).filter(Boolean)[0] ||
            expiredServiceTasks.map((task) => formatDeadline(task.serviceDeadline || task.exchangeDeadline)).filter(Boolean)[0];
          showNotice({
            tone: 'warning',
            title: '處理時間已截止',
            message: `這筆售後資料已超過可處理時間${deadline ? `（截止：${deadline}）` : ''}，目前無法在線上送出。如需協助請私訊客服。`,
          });
        } else {
          const completedExchangeDetails = rawResults
            .map((result) => result.exchangeItem || {})
            .filter((item) => item.customerSelection?.action === 'exchange')
            .map((item) => {
              const product = item.customerSelection?.productName || '換貨商品';
              const style = isDisplayableStyle(item.customerSelection?.style) ? ` / ${item.customerSelection.style}` : '';
              const amount = Math.max(0, Number(item.exchangeDifferenceRefundAmount || 0) || 0);
              const refundText = item.exchangeDifferenceRefundStatus === 'success' && amount > 0
                ? `・差額紅利已退 ${amount} 點`
                : item.exchangeDifferenceRefundStatus === 'sending'
                  ? '・差額紅利處理中'
                  : item.exchangeDifferenceRefundStatus === 'uncertain'
                    ? '・差額紅利請聯繫客服確認'
                    : '';
              return `已換貨：${product}${style}${refundText}`;
            });
          showNotice({
            tone: 'success',
            title: '這筆資料已完成',
            message: '這筆售後服務資料已送出或已處理，目前沒有需要處理的項目。',
            detail: completedExchangeDetails.join('\n'),
          });
        }
      } else if (!silentEmptyNotice) {
        if (effectiveLinkToken) backToSearch();
        showNotice({
          tone: 'warning',
          title: '查不到售後服務資料',
          message: '請確認 PA 單號或收件人手機是否正確。若仍查不到，請私訊客服協助。',
        });
      }
    } catch (error) {
      if (effectiveLinkToken) backToSearch();
      const expiredLink = effectiveLinkToken && /not-found|連結已失效/.test(`${error.code || ''} ${error.message || ''}`);
      if (expiredLink) {
        setNotice(null);
        setViewMode('linkExpired');
        setIsLoading(false);
        return;
      }
      showNotice({
        tone: 'error',
        title: '查詢失敗',
        message: '系統暫時無法完成查詢，請稍後再試。',
        detail: error.message,
      });
    }
    setIsLoading(false);
  };

  useEffect(() => {
    if (accessLinkToken) doSearch({ linkToken: accessLinkToken, lookupValue: '' });
    // Secure links intentionally auto-query once when the page opens.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessLinkToken]);

  useEffect(() => {
    if (!draftKey || viewMode === 'search') return undefined;
    const timer = window.setTimeout(() => {
      window.localStorage.setItem(draftKey, JSON.stringify({
        savedAt: Date.now(),
        serviceAnswers,
        serviceAnswerPlans,
        serviceSameAsLinks,
        collapsedServiceAnswers,
        selectedMap,
      }));
    }, 450);
    return () => window.clearTimeout(timer);
  }, [collapsedServiceAnswers, draftKey, selectedMap, serviceAnswerPlans, serviceAnswers, serviceSameAsLinks, viewMode]);

  const openOrderChoice = (choice, sourceResults = allResults, sourceServiceTasks = allServiceTasks) => {
    const key = choice.key;
    const resultSource = sourceResults.length > 0 ? sourceResults : results;
    const taskSource = sourceServiceTasks.length > 0 ? sourceServiceTasks : serviceTasks;
    setResults(resultSource.filter((result) => orderKeyOf(result) === key));
    setServiceTasks(taskSource.filter((task) => serviceTaskOrderKeyOf(task) === key));
    setSelectedMap({});
    setServiceAnswers({});
    setServiceAnswerPlans({});
    setServiceSameAsLinks({});
    setCollapsedServiceAnswers({});
    setServiceErrors({});
    setViewMode('exchange');
    window.setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 0);
  };

  const selectOrderChoice = (choice) => {
    openOrderChoice(choice);
  };

  const showNextOrderPrompt = ({
    currentKey,
    nextAllResults,
    nextAllServiceTasks,
    title,
    message,
  }) => {
    const remainingChoices = buildCombinedOrderChoices(nextAllResults, nextAllServiceTasks)
      .filter((choice) => choice.key !== currentKey && orderChoiceHasPendingWork(choice));
    if (remainingChoices.length === 0) {
      showNotice({ tone: 'success', title, message });
      return false;
    }
    const nextChoice = remainingChoices[0];
    showNotice({
      tone: 'success',
      title,
      message: `${message}\n這支電話還有 ${remainingChoices.length} 張付款單需要處理，要前往下一筆嗎？`,
      actionLabel: `前往 ${nextChoice.paNumber}`,
      onAction: () => {
        setNotice(null);
        openOrderChoice(nextChoice, nextAllResults, nextAllServiceTasks);
      },
      secondaryActionLabel: '選擇其他訂單',
      onSecondaryAction: () => {
        setNotice(null);
        setResults(nextAllResults);
        setServiceTasks(nextAllServiceTasks);
        setViewMode('orderSelect');
      },
    });
    return true;
  };

  const selectExchange = (item, product, style = '') => {
    if (isSubmitting) return;
    const productKey = getProductKey(product);
    const itemKey = getExchangeItemSelectionKey(item);
    setSelectedMap((prev) => {
      const current = prev[itemKey];
      const isSameSelection =
        current?.action === 'exchange' &&
        current.productId === productKey &&
        (current.style || '') === (style || '');
      if (isSameSelection) {
        const next = { ...prev };
        delete next[itemKey];
        return next;
      }
      return {
        ...prev,
        [itemKey]: {
        action: 'exchange',
        productId: productKey,
        jamboCommodityId: product.jamboCommodityId || product.commodityId || product.id,
        commodityId: product.commodityId || product.jamboCommodityId || product.id,
        productName: product.name,
        itemNumber: product.itemNumber,
        refundPriceDifference: item.refundPriceDifference === true,
        style,
      },
      };
    });
  };

  const selectCancel = (item) => {
    if (isSubmitting) return;
    const itemKey = getExchangeItemSelectionKey(item);
    setSelectedMap((prev) => {
      if (prev[itemKey]?.action === 'cancel') {
        const next = { ...prev };
        delete next[itemKey];
        return next;
      }
      return {
        ...prev,
        [itemKey]: { action: 'cancel', productName: '不換貨，申請退款', style: '' },
      };
    });
  };

  const submit = async (group) => {
    if (isSubmitting) return;
    const selections = {};
    group.items.forEach((item) => {
      const itemKey = getExchangeItemSelectionKey(item);
      if (selectedMap[itemKey]) selections[item.id] = selectedMap[itemKey];
    });
    if (Object.keys(selections).length !== group.items.length) {
      showNotice({
        tone: 'warning',
        title: '還有商品沒有選好',
        message: '請先完成每一項商品的換貨選擇；若不換貨，請選「不換貨」。',
      });
      return;
    }
    const missingStyleItem = group.items.find((item) => {
      const selection = selectedMap[getExchangeItemSelectionKey(item)];
      if (!selection || selection.action !== 'exchange') return false;
      const product = (item.availableExchanges || []).find((option) => (
        getProductKey(option) === selection.productId
      ));
      return productNeedsStyleChoice(product) && !selection.style;
    });
    if (missingStyleItem) {
      showNotice({
        tone: 'warning',
        title: '請選擇商品樣式',
        message: '有商品需要選擇樣式，請先在紅框處選好樣式後再送出。',
      });
      return;
    }
    setIsSubmitting(true);
    try {
      const submitExchange = httpsCallable(functions, 'tianlanSubmitJamboExchange');
      await submitExchange({
        shopUserId: group.shopUserId,
        storeName: group.storeName,
        paNumber: group.paNumber,
        selections,
        customerAccessToken,
      });
      const currentKey = `${group.shopUserId || ''}_${group.paNumber || ''}`;
      const resultSource = allResults.length > 0 ? allResults : results;
      const taskSource = allServiceTasks.length > 0 ? allServiceTasks : serviceTasks;
      const nextAllResults = resultSource.filter((result) => orderKeyOf(result) !== currentKey);
      setAllResults(nextAllResults);
      setResults(results.filter((result) => orderKeyOf(result) !== currentKey));
      setSelectedMap({});
      if (draftKey) window.localStorage.removeItem(draftKey);
      setViewMode('search');
      showNextOrderPrompt({
        currentKey,
        nextAllResults,
        nextAllServiceTasks: taskSource,
        title: '我們已收到您的選擇',
        message: '小幫手會依據您的選擇進行後續換貨流程。',
      });
    } catch (error) {
      const message = error.message || '';
      if (error.code === 'functions/failed-precondition' || /已被選完|可換庫存|重新查詢|截止|過期/.test(message)) {
        await doSearch({ silentEmptyNotice: true });
        showNotice({
          tone: 'warning',
          title: /截止|過期/.test(message) ? '換貨時間已截止' : '庫存已更新',
          message: /截止|過期/.test(message)
            ? '這筆換貨資料已超過可選擇時間，目前無法在線上送出選擇。如需協助請私訊客服。'
            : '有品項已被選完或庫存剛剛更新，頁面已重新載入最新可選商品，請再選擇一次。',
          detail: message,
        });
      } else {
        showNotice({
          tone: 'error',
          title: '送出失敗',
          message: '目前無法送出換貨選擇，請稍後再試或私訊客服協助。',
          detail: message,
        });
      }
    }
    setIsSubmitting(false);
  };

  const updateServiceAnswer = (taskId, question, value) => {
    const questionId = typeof question === 'string' ? question : question.id;
    const editedLinkKey = sameAsLinkKey(taskId, questionId);
    setServiceSameAsLinks((previous) => {
      if (!previous[editedLinkKey]) return previous;
      const next = { ...previous };
      delete next[editedLinkKey];
      return next;
    });
    setServiceAnswers((prev) => {
      const next = {
        ...prev,
        [taskId]: {
          ...(prev[taskId] || {}),
          [questionId]: value,
        },
      };
      Object.values(serviceSameAsLinks).forEach((link) => {
        if (link.sourcePlanId !== taskId || link.questionId !== questionId) return;
        next[link.targetPlanId] = {
          ...(next[link.targetPlanId] || {}),
          [questionId]: cloneServiceAnswer(value),
        };
      });
      return next;
    });
    if (typeof question === 'object' && question) {
      const error = validateServiceAnswer(question, value);
      setServiceErrors((prev) => {
        const taskErrors = { ...(prev[taskId] || {}) };
        if (error) {
          taskErrors[questionId] = error;
        } else {
          delete taskErrors[questionId];
        }
        const next = { ...prev };
        if (Object.keys(taskErrors).length > 0) {
          next[taskId] = taskErrors;
        } else {
          delete next[taskId];
        }
        return next;
      });
    }
  };

  const applySameAsAnswer = (targetPlanId, sourcePlanId, question) => {
    const sourceValue = serviceAnswers[sourcePlanId]?.[question.id];
    if (!serviceAnswerIsFilled(sourceValue)) return;
    const linkKey = sameAsLinkKey(targetPlanId, question.id);
    setServiceAnswers((previous) => ({
      ...previous,
      [targetPlanId]: {
        ...(previous[targetPlanId] || {}),
        [question.id]: cloneServiceAnswer(sourceValue),
      },
    }));
    setServiceSameAsLinks((previous) => ({
      ...previous,
      [linkKey]: {
        targetPlanId,
        sourcePlanId,
        questionId: question.id,
      },
    }));
    setServiceErrors((previous) => {
      const next = { ...previous };
      const planErrors = { ...(next[targetPlanId] || {}) };
      delete planErrors[question.id];
      if (Object.keys(planErrors).length > 0) next[targetPlanId] = planErrors;
      else delete next[targetPlanId];
      return next;
    });
  };

  const cancelSameAsAnswer = (targetPlanId, questionId) => {
    const linkKey = sameAsLinkKey(targetPlanId, questionId);
    setServiceSameAsLinks((previous) => {
      const next = { ...previous };
      delete next[linkKey];
      return next;
    });
  };

  const applySameAsAllAnswers = (targetPlanId, sourcePlanId, questions) => {
    const sourceAnswers = serviceAnswers[sourcePlanId] || {};
    const sourceQuestions = getVisibleServiceQuestions(questions, sourceAnswers)
      .filter((question) => serviceAnswerIsFilled(sourceAnswers[question.id]));
    if (sourceQuestions.length === 0) return;
    setServiceAnswers((previous) => ({
      ...previous,
      [targetPlanId]: sourceQuestions.reduce((nextAnswers, question) => ({
        ...nextAnswers,
        [question.id]: cloneServiceAnswer(sourceAnswers[question.id]),
      }), { ...(previous[targetPlanId] || {}) }),
    }));
    setServiceSameAsLinks((previous) => sourceQuestions.reduce((nextLinks, question) => ({
      ...nextLinks,
      [sameAsLinkKey(targetPlanId, question.id)]: {
        targetPlanId,
        sourcePlanId,
        questionId: question.id,
      },
    }), previous));
    setServiceErrors((previous) => {
      const next = { ...previous };
      const planErrors = { ...(next[targetPlanId] || {}) };
      sourceQuestions.forEach((question) => delete planErrors[question.id]);
      if (Object.keys(planErrors).length > 0) next[targetPlanId] = planErrors;
      else delete next[targetPlanId];
      return next;
    });
  };

  const cancelSameAsAllAnswers = (targetPlanId, sourcePlanId) => {
    setServiceSameAsLinks((previous) => Object.fromEntries(
      Object.entries(previous).filter(([, link]) => !(
        link.targetPlanId === targetPlanId && link.sourcePlanId === sourcePlanId
      )),
    ));
  };

  const addServiceAnswerPlan = (serviceGroup) => {
    setServiceAnswerPlans((previous) => {
      const current = previous[serviceGroup.key] || defaultAnswerPlansFor(serviceGroup);
      const donorIndex = current.reduce(
        (bestIndex, item, index) => (item.count > current[bestIndex].count ? index : bestIndex),
        0,
      );
      if (!current[donorIndex] || current[donorIndex].count <= 1) return previous;
      const nextPlans = current.map((item, index) => (
        index === donorIndex ? { ...item, count: item.count - 1 } : item
      ));
      nextPlans.push({ id: makeAnswerPlanId(), count: 1 });
      return { ...previous, [serviceGroup.key]: nextPlans };
    });
  };

  const updateServiceAnswerPlanCount = (serviceGroup, planId, requestedCount) => {
    setServiceAnswerPlans((previous) => {
      const current = previous[serviceGroup.key] || defaultAnswerPlansFor(serviceGroup);
      if (current.length <= 1) return previous;
      const total = serviceGroup.tasks.length;
      const desired = Math.max(1, Math.min(total - current.length + 1, Math.floor(Number(requestedCount || 1) || 1)));
      let remaining = total - desired;
      const otherPlans = current.filter((item) => item.id !== planId);
      const nextOthers = otherPlans.map((item, index) => {
        const laterCount = otherPlans.length - index - 1;
        const count = index === otherPlans.length - 1
          ? remaining
          : Math.max(1, Math.min(Number(item.count || 1), remaining - laterCount));
        remaining -= count;
        return { ...item, count };
      });
      const nextPlans = current.map((item) => (
        item.id === planId
          ? { ...item, count: desired }
          : nextOthers.find((other) => other.id === item.id)
      ));
      return { ...previous, [serviceGroup.key]: nextPlans };
    });
  };

  const removeServiceAnswerPlan = (serviceGroup, planId) => {
    const current = serviceAnswerPlans[serviceGroup.key] || defaultAnswerPlansFor(serviceGroup);
    if (current.length <= 1) return;
    const removed = current.find((item) => item.id === planId);
    const remaining = current.filter((item) => item.id !== planId);
    if (remaining.length > 0 && removed) remaining[0] = { ...remaining[0], count: remaining[0].count + removed.count };
    setServiceAnswerPlans((previous) => ({ ...previous, [serviceGroup.key]: remaining }));
    setServiceAnswers((previous) => {
      const next = { ...previous };
      delete next[planId];
      return next;
    });
    setServiceErrors((previous) => {
      const next = { ...previous };
      delete next[planId];
      return next;
    });
    setServiceSameAsLinks((previous) => Object.fromEntries(
      Object.entries(previous).filter(([, link]) => (
        link.targetPlanId !== planId && link.sourcePlanId !== planId
      )),
    ));
  };

  const submitServiceTasks = async (group) => {
    if (isSubmitting) return;
    const pendingTasks = (group.serviceTasks || []).filter((task) => (
      isPendingServiceTask(task) && !isExpiredServiceTask(task)
    ));
    if (pendingTasks.length === 0) {
      showNotice({
        tone: 'info',
        title: '目前沒有需要送出的表單',
        message: '這張付款單的表單已送出、已處理或已超過可填寫時間。',
      });
      return;
    }

    const pendingServiceGroups = groupServiceFormTasks(pendingTasks);
    const submissionEntries = [];
    const nextErrorsByPlan = {};
    const nextErrorList = [];
    pendingServiceGroups.forEach((serviceGroup) => {
      const task = serviceGroup.tasks[0];
      const template = serviceTemplates[task.questionTemplateId || task.formId] || {};
      const questions = Array.isArray(template.questions) ? template.questions : [];
      const plans = serviceAnswerPlans[serviceGroup.key] || defaultAnswerPlansFor(serviceGroup);
      let taskCursor = 0;
      plans.forEach((plan, planIndex) => {
        const planAnswers = serviceAnswers[plan.id] || {};
        const planErrors = {};
        getVisibleServiceQuestions(questions, planAnswers).forEach((question) => {
          const error = validateServiceAnswer(question, planAnswers[question.id]);
          if (error) {
            const productName = task.product?.name || task.originalProduct?.name || '售後服務商品';
            planErrors[question.id] = error;
            nextErrorList.push(`${productName} - ${labelServiceAnswerPlan(planIndex, template)} - ${question.label || '填寫項目'}：${error}`);
          }
        });
        if (Object.keys(planErrors).length > 0) nextErrorsByPlan[plan.id] = planErrors;
        const responses = sanitizeVisibleServiceResponses(questions, planAnswers);
        serviceGroup.tasks
          .slice(taskCursor, taskCursor + plan.count)
          .forEach((planTask) => submissionEntries.push({ task: planTask, responses }));
        taskCursor += plan.count;
      });
    });

    setServiceErrors((prev) => {
      const next = { ...prev };
      pendingServiceGroups.forEach((serviceGroup) => {
        const plans = serviceAnswerPlans[serviceGroup.key] || defaultAnswerPlansFor(serviceGroup);
        plans.forEach((plan) => {
          if (nextErrorsByPlan[plan.id]) {
            next[plan.id] = nextErrorsByPlan[plan.id];
          } else {
            delete next[plan.id];
          }
        });
      });
      return next;
    });

    if (submissionEntries.length !== pendingTasks.length) {
      showNotice({
        tone: 'warning',
        title: '份數分配需要更新',
        message: '表單份數剛剛有變動，請再確認一次後送出。',
      });
      return;
    }

    if (Object.keys(nextErrorsByPlan).length > 0) {
      showNotice({
        tone: 'warning',
        title: '請先修正表單內容',
        message: `有 ${nextErrorList.length} 個欄位需要修正，請查看紅框欄位與下方提示後再送出。`,
        detail: nextErrorList.slice(0, 8).join('\n'),
      });
      return;
    }

    setIsSubmitting(true);
    try {
      const submitService = httpsCallable(functions, 'tianlanSubmitServiceTasksBatch');
      await submitService({
        customerAccessToken,
        submissions: submissionEntries.map(({ task, responses }) => ({ taskId: task.id, responses })),
      });
      const submittedTaskIds = new Set(submissionEntries.map(({ task }) => task.id));
      const removeSubmittedTasks = (items) => items.filter((item) => !submittedTaskIds.has(item.id));
      const nextServiceTasks = removeSubmittedTasks(serviceTasks);
      const nextAllServiceTasks = removeSubmittedTasks(allServiceTasks.length > 0 ? allServiceTasks : serviceTasks);
      setServiceTasks(nextServiceTasks);
      setAllServiceTasks(nextAllServiceTasks);
      if (draftKey) window.localStorage.removeItem(draftKey);
      const currentKey = `${group.shopUserId || ''}_${group.paNumber || ''}`;
      const hasCurrentExchangeItems = (results || []).some((result) => orderKeyOf(result) === currentKey);
      const hasCurrentPendingServiceTasks = nextAllServiceTasks.some((task) => serviceTaskOrderKeyOf(task) === currentKey);
      if (!hasCurrentExchangeItems && !hasCurrentPendingServiceTasks) setViewMode('search');
      showNextOrderPrompt({
        currentKey,
        nextAllResults: allResults.length > 0 ? allResults : results,
        nextAllServiceTasks,
        title: '我們已收到您的資料',
        message: pendingTasks.length > 1
          ? `共 ${pendingTasks.length} 份表單已一次送出，小幫手會依據您填寫的內容進行後續處理。`
          : '小幫手會依據您填寫的內容進行後續處理。',
      });
    } catch (error) {
      showNotice({
        tone: 'error',
        title: /截止|過期/.test(error.message || '') ? '處理時間已截止' : '送出失敗',
        message: /截止|過期/.test(error.message || '')
          ? '售後資料已超過可填寫時間，目前無法在線上送出。如需協助請私訊客服。'
          : '目前無法送出資料，請稍後再試或私訊客服協助。',
        detail: error.message,
      });
    }
    setIsSubmitting(false);
  };

  const serviceErrorSummaryForGroup = (group) => {
    const rows = [];
    const pendingGroups = groupServiceFormTasks(
      (group.serviceTasks || []).filter((task) => isPendingServiceTask(task) && !isExpiredServiceTask(task)),
    );
    pendingGroups.forEach((serviceGroup) => {
      const task = serviceGroup.tasks[0];
      const plans = serviceAnswerPlans[serviceGroup.key] || defaultAnswerPlansFor(serviceGroup);
      plans.forEach((plan, planIndex) => {
        const template = serviceTemplates[task.questionTemplateId || task.formId] || {};
        const questions = Array.isArray(template.questions) ? template.questions : [];
        const taskErrors = serviceErrors[plan.id] || {};
        const productName = task.product?.name || task.originalProduct?.name || '售後服務商品';
        getVisibleServiceQuestions(questions, serviceAnswers[plan.id] || {}).forEach((question) => {
          const error = taskErrors[question.id];
          if (!error) return;
          rows.push({
            key: `${plan.id}_${question.id}`,
            productName: `${productName} / ${labelServiceAnswerPlan(planIndex, template)}`,
            questionLabel: question.label || '填寫項目',
            error,
          });
        });
      });
    });
    return rows;
  };

  const renderServiceField = (task, question, index, options = {}) => {
    const value = serviceAnswers[task.id]?.[question.id] || '';
    const taskErrors = serviceErrors[task.id] || {};
    const error = taskErrors[question.id] || '';
    const textLength = normalizeText(value).length;
    const minLength = Number(question.minLength || 0) || 0;
    const maxLength = Number(question.maxLength || 0) || 0;
    const hasTextLimit = question.type === 'text' && (minLength > 0 || maxLength > 0);
    const dateParts = question.type === 'date' ? parseServiceDateValue(value) : {};
    const calendarDateParts = question.type === 'calendar_date' && value && typeof value === 'object'
      ? value
      : { calendar: '', year: '', month: '', day: '' };
    const currentYear = new Date().getFullYear();
    const yearOptions = Array.from({ length: 122 }, (_, optionIndex) => String(currentYear - optionIndex));
    const monthOptions = Array.from({ length: 12 }, (_, optionIndex) => padDateNumber(optionIndex + 1));
    const dayOptions = Array.from(
      { length: daysInServiceMonth(dateParts.year, dateParts.month) },
      (_, optionIndex) => padDateNumber(optionIndex + 1),
    );
    const calendarDayCount = calendarDateParts.calendar === 'lunar'
      ? 30
      : daysInServiceMonth(calendarDateParts.year, calendarDateParts.month);
    const calendarDayOptions = Array.from(
      { length: calendarDayCount },
      (_, optionIndex) => padDateNumber(optionIndex + 1),
    );
    const dateMode = question.dateMode || 'date';
    const sameAsSourcePlanId = options.sameAsSourcePlanId || '';
    const sameAsSourceLabel = options.sameAsSourceLabel || '上一項商品';
    const sameAsKey = sameAsLinkKey(task.id, question.id);
    const sameAsChecked = Boolean(serviceSameAsLinks[sameAsKey]);
    const sameAsSourceValue = sameAsSourcePlanId
      ? serviceAnswers[sameAsSourcePlanId]?.[question.id]
      : undefined;
    const sameAsSourceFilled = serviceAnswerIsFilled(sameAsSourceValue);
    return (
      <div className={`tl-customer-form-field ${error ? 'has-error' : ''}`} key={question.id}>
        <span>
          <b>{index + 1}</b>
          <strong>{question.label}</strong>
          {question.required && <em>必填</em>}
          {sameAsSourcePlanId && (
            <label
              className={`tl-same-as-control ${sameAsChecked ? 'is-selected' : ''} ${sameAsSourceFilled ? '' : 'is-disabled'}`}
              title={sameAsSourceFilled
                ? `同步「${sameAsSourceLabel}」的這題答案`
                : `請先填寫「${sameAsSourceLabel}」的這題答案`}
            >
              <input
                type="checkbox"
                checked={sameAsChecked}
                disabled={!sameAsSourceFilled}
                onChange={(event) => {
                  if (event.target.checked) applySameAsAnswer(task.id, sameAsSourcePlanId, question);
                  else cancelSameAsAnswer(task.id, question.id);
                }}
              />
              <strong>同上</strong>
            </label>
          )}
        </span>
        {question.type === 'text' && (
          <>
            <textarea
              value={value}
              onChange={(event) => updateServiceAnswer(task.id, question, event.target.value)}
              onBlur={() => updateServiceAnswer(task.id, question, value)}
              placeholder={question.maxLength ? `最多 ${question.maxLength} 個字` : '請輸入'}
              aria-invalid={Boolean(error)}
            />
            {hasTextLimit && (
              <span className={`tl-customer-char-counter ${error ? 'has-error' : ''}`}>
                目前 {textLength} 個字
                {minLength > 0 && ` / 至少 ${minLength} 個字`}
                {maxLength > 0 && ` / 最多 ${maxLength} 個字`}
              </span>
            )}
          </>
        )}
        {question.type === 'date' && (
          <div className="tl-date-wheel-wrap">
            <div className="tl-date-wheel-grid" aria-invalid={Boolean(error)}>
              <label className="tl-date-wheel-column">
                <span>年</span>
                <select
                  className="tl-customer-date-select"
                  value={dateParts.year || ''}
                  onChange={(event) => updateServiceAnswer(task.id, question, updateServiceDatePart(value, 'year', event.target.value, dateMode))}
                >
                  <option value="">請選擇</option>
                  {yearOptions.map((year) => (
                    <option key={year} value={year}>{year}</option>
                  ))}
                </select>
              </label>
              <label className="tl-date-wheel-column">
                <span>月</span>
                <select
                  className="tl-customer-date-select"
                  value={dateParts.month || ''}
                  onChange={(event) => updateServiceAnswer(task.id, question, updateServiceDatePart(value, 'month', event.target.value, dateMode))}
                >
                  <option value="">請選擇</option>
                  {monthOptions.map((month) => (
                    <option key={month} value={month}>{month}</option>
                  ))}
                </select>
              </label>
              <label className="tl-date-wheel-column">
                <span>日</span>
                <select
                  className="tl-customer-date-select"
                  value={dateParts.day || ''}
                  onChange={(event) => updateServiceAnswer(task.id, question, updateServiceDatePart(value, 'day', event.target.value, dateMode))}
                >
                  <option value="">請選擇</option>
                  {dayOptions.map((day) => (
                    <option key={day} value={day}>{day}</option>
                  ))}
                </select>
              </label>
            </div>
            {dateMode === 'datetime' && (
              <label className="tl-date-time-column">
                <span>時間</span>
                <input
                  className="tl-customer-date-input"
                  type="time"
                  value={dateParts.time || ''}
                  onChange={(event) => updateServiceAnswer(task.id, question, updateServiceDatePart(value, 'time', event.target.value, dateMode))}
                  aria-invalid={Boolean(error)}
                />
              </label>
            )}
          </div>
        )}
        {question.type === 'calendar_date' && (
          <div className="tl-calendar-date-wrap">
            <div className="tl-calendar-segment" role="radiogroup" aria-label="選擇國曆或農曆">
              {[
                { value: 'gregorian', label: '國曆', caption: '西元年月日' },
                { value: 'lunar', label: '農曆', caption: '農曆年月日' },
              ].map((calendar) => (
                <button
                  type="button"
                  key={calendar.value}
                  className={`tl-calendar-choice ${calendarDateParts.calendar === calendar.value ? 'selected' : ''}`.trim()}
                  onClick={() => updateServiceAnswer(task.id, question, updateCalendarDatePart(calendarDateParts, 'calendar', calendar.value))}
                  role="radio"
                  aria-checked={calendarDateParts.calendar === calendar.value}
                >
                  <span className="tl-calendar-choice-copy">
                    <strong>{calendar.label}</strong>
                    <span>{calendar.caption}</span>
                  </span>
                  <span className="tl-calendar-radio-dot" aria-hidden="true" />
                </button>
              ))}
            </div>
            {calendarDateParts.calendar && (
              <div className="tl-date-wheel-grid" aria-invalid={Boolean(error)}>
                <label className="tl-date-wheel-column">
                  <span>年</span>
                  <select
                    className="tl-customer-date-select"
                    value={calendarDateParts.year || ''}
                    onChange={(event) => updateServiceAnswer(task.id, question, updateCalendarDatePart(calendarDateParts, 'year', event.target.value))}
                  >
                    <option value="">請選擇</option>
                    {yearOptions.map((year) => <option key={year} value={year}>{year}</option>)}
                  </select>
                </label>
                <label className="tl-date-wheel-column">
                  <span>月</span>
                  <select
                    className="tl-customer-date-select"
                    value={calendarDateParts.month || ''}
                    onChange={(event) => updateServiceAnswer(task.id, question, updateCalendarDatePart(calendarDateParts, 'month', event.target.value))}
                  >
                    <option value="">請選擇</option>
                    {monthOptions.map((month) => <option key={month} value={month}>{month}</option>)}
                  </select>
                </label>
                <label className="tl-date-wheel-column">
                  <span>日</span>
                  <select
                    className="tl-customer-date-select"
                    value={calendarDateParts.day || ''}
                    onChange={(event) => updateServiceAnswer(task.id, question, updateCalendarDatePart(calendarDateParts, 'day', event.target.value))}
                  >
                    <option value="">請選擇</option>
                    {calendarDayOptions.map((day) => <option key={day} value={day}>{day}</option>)}
                  </select>
                </label>
              </div>
            )}
          </div>
        )}
        {question.type === 'birth_time' && (
          <NativeBirthTimeSelect
            value={value}
            options={BIRTH_TIME_OPTIONS}
            label={question.label || '請選擇出生時辰'}
            hasError={Boolean(error)}
            onChange={(option) => updateServiceAnswer(task.id, question, option)}
          />
        )}
        {question.type === 'choice' && (
          <div className="tl-choice-list">
            {(question.options || []).map((option) => (
              <button
                type="button"
                key={option}
                className={value === option ? 'selected' : ''}
                onClick={() => updateServiceAnswer(task.id, question, option)}
                aria-pressed={value === option}
              >
                {option}
              </button>
            ))}
          </div>
        )}
        {error && <small className="tl-customer-field-error">{error}</small>}
      </div>
    );
  };

  const renderServiceTask = (task, options = {}) => {
    const template = serviceTemplates[task.questionTemplateId || task.formId] || {};
    const questions = Array.isArray(template.questions) ? template.questions : [];
    const taskAnswers = serviceAnswers[task.id] || {};
    const visibleQuestions = getVisibleServiceQuestions(questions, taskAnswers);
    const statusText = serviceTaskStatusText(task);
    const expired = isExpiredServiceTask(task);
    const submitted = isSubmittedServiceTask(task);
    const imageUrl = task.product?.image || task.originalProduct?.image || '';
    const productName = task.product?.name || task.originalProduct?.name || '售後服務商品';
    const itemNumber = task.product?.partNumber || task.product?.itemNumber || task.originalProduct?.itemNumber || '';
    const styleText = isDisplayableStyle(task.originalStyle) ? task.originalStyle : '';
    const combinedParentName = task.formProductMode === 'children'
      ? (task.combinedParentProduct?.name || task.combinedParentProduct?.partNumber || '')
      : '';
    const deadlineText = formatDeadline(task.serviceDeadline || task.exchangeDeadline);
    const answerPlan = options.answerPlan;
    const serviceGroup = options.serviceGroup;
    const answerPlanIndex = Number(options.answerPlanIndex || 0);
    const sameAsSourcePlanId = options.sameAsSourcePlanId || '';
    const sameAsSourceLabel = options.sameAsSourceLabel || '上一項商品';
    const showProductDetails = options.showProductDetails !== false;
    const plans = serviceGroup
      ? (serviceAnswerPlans[serviceGroup.key] || defaultAnswerPlansFor(serviceGroup))
      : [];
    const canCollapse = answerPlan && template.allowQuestionCollapse === true;
    const canAdjustAnswerGroupCount = isAnswerGroupCountAdjustEnabled(template);
    const defaultCollapsed = canAdjustAnswerGroupCount && answerPlanIndex > 0;
    const isCollapsed = canCollapse && (collapsedServiceAnswers[task.id] ?? defaultCollapsed);
    const planProgress = answerPlan
      ? getServiceAnswerPlanProgress(questions, [answerPlan], serviceAnswers).rows[0]
      : null;
    const sourceAnswers = sameAsSourcePlanId ? (serviceAnswers[sameAsSourcePlanId] || {}) : {};
    const sameAsQuestions = sameAsSourcePlanId
      ? getVisibleServiceQuestions(questions, sourceAnswers)
        .filter((question) => serviceAnswerIsFilled(sourceAnswers[question.id]))
      : [];
    const allSameAsLinked = sameAsQuestions.length > 0 && sameAsQuestions.every((question) => Boolean(
      serviceSameAsLinks[sameAsLinkKey(task.id, question.id)],
    ));
    return (
      <article className={`tl-customer-service-card ${submitted ? 'submitted' : ''} ${expired ? 'expired' : ''}`} key={task.id}>
        {answerPlan && serviceGroup && (serviceGroup.tasks.length > 1 || sameAsQuestions.length > 0) && (
          <div className="tl-answer-group-toolbar">
            <div className="tl-answer-group-title">
              <span>{labelServiceAnswerPlan(answerPlanIndex, template)}</span>
              <strong>
                {canAdjustAnswerGroupCount && `${answerPlan.count} 份 · `}
                {planProgress?.complete ? '已完成' : `尚有 ${planProgress?.errorCount || 0} 項未完成`}
              </strong>
            </div>
            {serviceGroup.tasks.length > 1 && canAdjustAnswerGroupCount && (
              <label>
                <span>套用份數</span>
                <input
                  type="number"
                  min="1"
                  max={serviceGroup.tasks.length - plans.length + 1}
                  value={answerPlan.count}
                  disabled={plans.length <= 1}
                  onChange={(event) => updateServiceAnswerPlanCount(serviceGroup, answerPlan.id, event.target.value)}
                />
              </label>
            )}
            <div className="tl-answer-group-actions">
              {sameAsQuestions.length > 0 && (
                <button
                  type="button"
                  className={allSameAsLinked ? 'is-linked' : ''}
                  onClick={() => {
                    if (allSameAsLinked) cancelSameAsAllAnswers(task.id, sameAsSourcePlanId);
                    else applySameAsAllAnswers(task.id, sameAsSourcePlanId, questions);
                  }}
                >
                  {allSameAsLinked
                    ? `已同步${sameAsSourceLabel}`
                    : `套用${sameAsSourceLabel}全部答案`}
                </button>
              )}
              {canCollapse && (
                <button
                  type="button"
                  onClick={() => setCollapsedServiceAnswers((previous) => ({
                    ...previous,
                    [task.id]: !isCollapsed,
                  }))}
                >
                  {isCollapsed ? '展開問題' : '收合問題'}
                </button>
              )}
              {canRemoveServiceAnswerPlan(plans, template) && (
                <button type="button" onClick={() => removeServiceAnswerPlan(serviceGroup, answerPlan.id)}>移除這組</button>
              )}
            </div>
          </div>
        )}
        {showProductDetails && (
          <>
            <div className="tl-service-card-product">
              {imageUrl ? (
                <img src={imageUrl} alt={productName} />
              ) : (
                <div>無圖</div>
              )}
              <div className="tl-service-card-copy">
                {combinedParentName && <span className="tl-service-combined-parent">組合商品｜{combinedParentName}</span>}
                <h2>{productName}</h2>
                <p>{[itemNumber, styleText].filter(Boolean).join(' ／ ')}</p>
              </div>
              <strong className="tl-service-task-state">{statusText}</strong>
            </div>
            <div className="tl-customer-form-overview">
              <div>
                <span>填寫項目</span>
                <strong>{visibleQuestions.length} 項</strong>
              </div>
              {deadlineText && (
                <div>
                  <span>截止</span>
                  <strong>{deadlineText}</strong>
                </div>
              )}
            </div>
            {(template.description || deadlineText) && (
              <div className="tl-customer-form-note">
                {template.description}
                {deadlineText && <small>請於 {deadlineText} 前完成填寫。</small>}
              </div>
            )}
          </>
        )}
        {isCollapsed ? (
          <button
            type="button"
            className="tl-collapsed-answer-hint"
            onClick={() => setCollapsedServiceAnswers((previous) => ({ ...previous, [task.id]: false }))}
          >
            {planProgress?.complete ? '答案已完成，點一下可展開修改。' : `尚有 ${planProgress?.errorCount || 0} 項未完成，點一下繼續填寫。`}
          </button>
        ) : expired ? (
          <div className="tl-customer-submitted-box">
            處理時間已截止
            <small>如需協助請私訊客服。</small>
          </div>
        ) : submitted ? (
          <div className="tl-customer-submitted-box">
            已送出資料
            <small>{task.submittedAt ? formatDeadline(task.submittedAt) : ''}</small>
          </div>
        ) : (
          <div className="tl-customer-form-stack">
            <div className="tl-customer-form-section-title">
              <span>填寫資料</span>
              <small>請確認內容正確</small>
            </div>
            {visibleQuestions.map((question, index) => renderServiceField(task, question, index, {
              sameAsSourcePlanId,
              sameAsSourceLabel,
            }))}
          </div>
        )}
      </article>
    );
  };

  const returnToOrders = () => {
    setResults(allResults);
    setServiceTasks(allServiceTasks);
    setViewMode('orderSelect');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };
  const hasOtherOrders = buildCombinedOrderChoices(allResults, allServiceTasks).length > 1;
  const backToSearch = () => {
    setAccessLinkToken('');
    const url = new URL(window.location.href);
    url.searchParams.delete('t');
    window.history.replaceState(null, '', url.pathname + url.search + url.hash);
    setViewMode('search');
    setResults([]);
    setServiceTasks([]);
    setAllResults([]);
    setAllServiceTasks([]);
    setSelectedMap({});
    setServiceAnswers({});
    setServiceAnswerPlans({});
    setServiceSameAsLinks({});
    setCollapsedServiceAnswers({});
    setServiceErrors({});
    setCustomerAccessToken('');
    setDraftKey('');
    window.setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 0);
  };

  return (
    <div
      className={`tianlan-customer-query ${viewMode !== 'search' ? 'tl-customer-exchange-mode' : 'tl-customer-search-mode'}`}
      data-design="clarity-a"
      style={{
        '--tl-customer-page-bg': customerTheme.pageBackgroundColor,
        '--tl-customer-card-bg': customerTheme.cardBackgroundColor,
        '--tl-customer-tablet-bg': customerTheme.tabletFrameBackgroundColor,
        '--tl-customer-button-bg': customerTheme.buttonColor,
        '--tl-customer-accent': customerTheme.accentColor,
      }}
    >
      {accessLinkToken && isLoading && <TianlanSecureLinkLoading />}
      {viewMode === 'linkExpired' && (
        <section className="tl-secure-loading tl-secure-expired" aria-labelledby="tl-expired-title">
          <div className="tl-secure-loading-mark">
            <img src={CUSTOMER_LOGO_URL} alt="天藍小舖" />
          </div>
          <h1 id="tl-expired-title">連結已失效</h1>
          <p>此連結已過期或無法使用。<br />請回首頁，以收件人手機或 PA 單號查詢訂單。</p>
          <button type="button" className="tl-secure-home-button" onClick={backToSearch}>回首頁</button>
        </section>
      )}
      {viewMode === 'search' && !(accessLinkToken && isLoading) && (
        <section className="tl-customer-stage">
        <div className="search-section tl-customer-search-card tl-customer-search-card-compact">
          <div className="brand-section tl-customer-brand-compact">
            <img
              src={CUSTOMER_LOGO_URL}
              alt="天藍小舖"
              className="brand-logo"
            />
            <div>
              <h1>訂單售後服務</h1>
            </div>
          </div>
          <div className="tl-customer-search-head">
            <div>
              <div className="tl-customer-search-label">先查詢您的待處理項目</div>
              <p>輸入完整 PA 單號或收件人手機即可開始</p>
            </div>
            <div className="tl-customer-query-status">
              <span>{isLoading ? '正在查詢' : results.length > 0 ? '已載入資料' : '等待輸入'}</span>
            </div>
          </div>
          <div className="tl-customer-search-box">
            <div className="tl-customer-input-wrap">
              <span>PA / Phone</span>
              <input
                className="search-input"
                value={search}
                onChange={(e) => setSearch(e.target.value.replace(/[a-z]/g, (m) => m.toUpperCase()))}
                onKeyDown={(e) => e.key === 'Enter' && doSearch()}
                placeholder="輸入 PA 單號或收件人電話"
              />
            </div>
            <button className="search-button" onClick={doSearch} disabled={isLoading}>
              {isLoading ? '查詢中...' : '開始查詢'}
            </button>
          </div>
          {announcement.content && (
            <div
              className="tl-customer-announcement-inline"
              style={{
                '--tl-announcement-bg': announcement.style.backgroundColor,
                '--tl-announcement-text': announcement.style.textColor,
              }}
            >
              {announcement.content}
            </div>
          )}
          <div className="tl-customer-search-foot">
            <details className="tl-customer-query-help">
              <summary>找不到 PA 單號或查不到資料？</summary>
              <p>PA 單號會顯示在付款單上；手機請輸入收件人完整號碼。仍查不到時可私訊客服。</p>
            </details>
            <button type="button" onClick={() => window.open(CUSTOMER_SUPPORT_URL, '_blank', 'noopener,noreferrer')}>聯絡客服</button>
          </div>
        </div>
        </section>
      )}

      {viewMode === 'orderSelect' && orderChoices.length > 0 && (
        <section className="tl-customer-exchange-shell">
          <div className="tl-customer-exchange-header tl-customer-result-header">
            <img src={CUSTOMER_LOGO_URL} alt="天藍小舖" className="tl-customer-result-logo" />
            <div>
              <span>付款單選擇</span>
              <h1>請選擇要處理的訂單編號</h1>
            </div>
            <button type="button" className="tl-customer-back-button" onClick={backToSearch}>
              重新查詢
            </button>
          </div>

          <div className="tl-customer-order-select-grid">
            {orderChoices.map((choice) => (
              <button
                type="button"
                key={choice.key}
                className="tl-customer-order-select-card"
                aria-label={`選擇付款單 ${choice.paNumber}`}
                onClick={() => selectOrderChoice(choice)}
              >
                <div className="tl-customer-order-select-copy">
                  <span className="tl-customer-order-select-kicker">訂單編號</span>
                  <strong>{choice.paNumber}</strong>
                  <small>{(choice.items || []).length + (choice.serviceTasks || []).length} 項待處理</small>
                </div>
                <span className="tl-customer-order-select-action" aria-hidden="true">
                  <b>進入處理</b>
                  <i />
                </span>
              </button>
            ))}
          </div>
        </section>
      )}

      {viewMode === 'exchange' && grouped.length > 0 && (
        <section className="tl-customer-exchange-shell">
          <div className="tl-customer-exchange-header tl-customer-result-header">
            <img src={CUSTOMER_LOGO_URL} alt="天藍小舖" className="tl-customer-result-logo" />
            <div>
              <span>售後服務</span>
              <h1>售後資料填寫</h1>
            </div>
            <button type="button" className="tl-customer-back-button" onClick={hasOtherOrders ? returnToOrders : backToSearch}>
              {hasOtherOrders ? '返回訂單選擇' : '查詢其他訂單'}
            </button>
          </div>

          <div className="tl-customer-results">
            {grouped.map((group) => {
              const groupSelectedCount = group.items.filter((item) => selectedMap[getExchangeItemSelectionKey(item)]).length;
              const hasExchangeItems = group.items.length > 0;
              const hasServiceTasks = (group.serviceTasks || []).length > 0;
              const groupReady = hasExchangeItems && groupSelectedCount === group.items.length;
              const remainingCount = group.items.length - groupSelectedCount;
              return (
                <div className={`modern-card tl-customer-order-card ${groupReady ? 'ready' : ''} ${!hasExchangeItems && hasServiceTasks ? 'tl-service-only-order' : ''}`} key={`${group.shopUserId}_${group.paNumber}`}>
                  <div className="modern-card-header tl-customer-order-head">
                    <div>
                      <div className="tl-customer-order-kicker">付款單</div>
                      <div className="modern-card-title">PA 單號：{group.paNumber}</div>
                    </div>
                    <div className={`tl-customer-order-progress ${groupReady ? 'ready' : ''}`}>
                      {hasExchangeItems
                        ? (groupReady ? '可送出' : `還差 ${remainingCount} 項`)
                        : `${groupServiceFormTasks(group.serviceTasks || []).length} 項商品`}
                    </div>
                  </div>
                  {hasExchangeItems && (
                    <div className="tl-customer-order-guidance">
                      {groupReady
                        ? '所有商品都已選好，請確認後送出。'
                        : '請逐項選擇換貨商品；若不換貨，請選「不換貨」。'}
                    </div>
                  )}
                  {hasExchangeItems && (
                    <>
                      <div className="tl-customer-item-list">
                        {group.items.map((item, index) => (
                          <JamboExchangeItem
                            key={getExchangeItemSelectionKey(item)}
                            item={item}
                            index={index}
                            selectedExchange={selectedMap[getExchangeItemSelectionKey(item)]}
                            onExchangeSelect={selectExchange}
                            onCancelExchange={selectCancel}
                            isDisabled={isSubmitting}
                          />
                        ))}
                      </div>
                      <button
                        className="confirm-button-mobile ready tl-customer-submit"
                        onClick={() => submit(group)}
                        disabled={isLoading || isSubmitting || !groupReady}
                      >
                        {isSubmitting ? (
                          <>
                            <span className="tl-button-spinner" aria-hidden="true" />
                            <span>正在送出，請稍候...</span>
                          </>
                        ) : groupReady ? '確認送出換貨選擇' : `請先完成 ${remainingCount} 項選擇`}
                      </button>
                    </>
                  )}
                  {(group.serviceTasks || []).length > 0 && (() => {
                    const pendingServiceTasks = (group.serviceTasks || []).filter((task) => (
                      isPendingServiceTask(task) && !isExpiredServiceTask(task)
                    ));
                    const pendingServiceGroups = groupServiceFormTasks(pendingServiceTasks);
                    const serviceErrorRows = serviceErrorSummaryForGroup(group);
                    const answerProgress = pendingServiceGroups.reduce((summary, serviceGroup) => {
                      const firstTask = serviceGroup.tasks[0];
                      const template = serviceTemplates[firstTask.questionTemplateId || firstTask.formId || ''] || {};
                      const questions = Array.isArray(template.questions) ? template.questions : [];
                      const plans = serviceAnswerPlans[serviceGroup.key] || defaultAnswerPlansFor(serviceGroup);
                      const progress = getServiceAnswerPlanProgress(questions, plans, serviceAnswers);
                      return {
                        total: summary.total + progress.total,
                        incomplete: summary.incomplete + progress.incomplete,
                      };
                    }, { total: 0, incomplete: 0 });
                    return (
                      <>
                        <div className="tl-customer-service-task-list">
                          {pendingServiceGroups.map((serviceGroup, serviceGroupIndex) => {
                            const plans = serviceAnswerPlans[serviceGroup.key] || defaultAnswerPlansFor(serviceGroup);
                            const firstTask = serviceGroup.tasks[0];
                            const templateId = firstTask.questionTemplateId || firstTask.formId || '';
                            const template = serviceTemplates[templateId] || {};
                            const questions = Array.isArray(template.questions) ? template.questions : [];
                            const progress = getServiceAnswerPlanProgress(questions, plans, serviceAnswers);
                            const sourceServiceGroup = pendingServiceGroups.find((candidate, candidateIndex) => (
                              candidateIndex < serviceGroupIndex &&
                              (candidate.tasks[0]?.questionTemplateId || candidate.tasks[0]?.formId || '') === templateId
                            ));
                            const sourcePlans = sourceServiceGroup
                              ? (serviceAnswerPlans[sourceServiceGroup.key] || defaultAnswerPlansFor(sourceServiceGroup))
                              : [];
                            const fallbackSameAsSourcePlanId = template.allowSameAsPrevious === true
                              ? sourcePlans[0]?.id || ''
                              : '';
                            const fallbackSameAsSourceLabel = sourceServiceGroup
                              ? (sourceServiceGroup.tasks[0]?.product?.name || sourceServiceGroup.tasks[0]?.originalProduct?.name || '上一項商品')
                              : '上一項商品';
                            const canAdjustAnswerGroupCount = isAnswerGroupCountAdjustEnabled(template);
                            const sourceQuantity = Number(firstTask.sourceQuantity || firstTask.originalQuantity || serviceGroup.tasks.length) || serviceGroup.tasks.length;
                            const remainingAnswerGroupCount = Math.max(serviceGroup.tasks.length - plans.length, 0);
                            const imageUrl = firstTask.product?.image || firstTask.originalProduct?.image || '';
                            const productName = firstTask.product?.name || firstTask.originalProduct?.name || '售後服務商品';
                            const itemNumber = firstTask.product?.partNumber || firstTask.product?.itemNumber || firstTask.originalProduct?.itemNumber || '';
                            const styleText = isDisplayableStyle(firstTask.originalStyle) ? firstTask.originalStyle : '';
                            const combinedParentName = firstTask.formProductMode === 'children'
                              ? (firstTask.combinedParentProduct?.name || firstTask.combinedParentProduct?.partNumber || '')
                              : '';
                            const deadlineText = formatDeadline(firstTask.serviceDeadline || firstTask.exchangeDeadline);
                            return (
                              <section className="tl-answer-group-shell" key={serviceGroup.key}>
                                <div className="tl-service-group-header">
                                  <div className="tl-service-card-product">
                                    {imageUrl ? <img src={imageUrl} alt={productName} /> : <div>無圖</div>}
                                    <div className="tl-service-card-copy">
                                      {combinedParentName && <span className="tl-service-combined-parent">組合商品｜{combinedParentName}</span>}
                                      <h2>{productName}</h2>
                                      <p>{[itemNumber, styleText].filter(Boolean).join(' ／ ')}</p>
                                    </div>
                                  </div>
                                  <div className="tl-service-group-progress" aria-label="填寫進度">
                                    <span>{serviceGroup.tasks.length} 份</span>
                                    {serviceGroup.tasks.length > 1 && canAdjustAnswerGroupCount && <span>{plans.length} 組答案</span>}
                                    <span className={progress.incomplete === 0 ? 'is-complete' : 'is-pending'}>
                                      {progress.incomplete === 0
                                        ? '已完成'
                                        : `${progress.incomplete} ${canAdjustAnswerGroupCount ? '組' : '份'}未完成`}
                                    </span>
                                  </div>
                                  {sourceQuantity !== serviceGroup.tasks.length && (
                                    <small className="tl-service-group-source-count">同一商品同一樣式共 {sourceQuantity} 件，可填 {serviceGroup.tasks.length} 份</small>
                                  )}
                                  {(template.description || deadlineText) && (
                                    <div className="tl-service-group-note">
                                      {template.description && <span>{template.description}</span>}
                                      {deadlineText && <small>請於 {deadlineText} 前完成</small>}
                                    </div>
                                  )}
                                </div>
                                {plans.map((plan, planIndex) => {
                                  const sameAsSource = getServiceAnswerPlanSameAsSource({
                                    plans,
                                    planIndex,
                                    template,
                                    fallbackPlanId: fallbackSameAsSourcePlanId,
                                    fallbackLabel: fallbackSameAsSourceLabel,
                                  });
                                  return renderServiceTask(
                                    { ...firstTask, id: plan.id },
                                    {
                                      answerPlan: plan,
                                      answerPlanIndex: planIndex,
                                      serviceGroup,
                                      sameAsSourcePlanId: sameAsSource.planId,
                                      sameAsSourceLabel: sameAsSource.label,
                                      showProductDetails: false,
                                    },
                                  );
                                })}
                                {canAdjustAnswerGroupCount && remainingAnswerGroupCount > 0 && (
                                  <div className="tl-answer-group-capacity">
                                    <div className="tl-answer-group-capacity-total">
                                      <span>最多可建立</span>
                                      <strong>{serviceGroup.tasks.length} 組答案</strong>
                                      <small>目前 {plans.length} 組</small>
                                    </div>
                                    <button
                                      type="button"
                                      className="tl-add-answer-group"
                                      onClick={() => addServiceAnswerPlan(serviceGroup)}
                                    >
                                      <span aria-hidden="true">＋</span>
                                      新增一組
                                    </button>
                                    <div className="tl-answer-group-capacity-remaining">
                                      <span>還可新增</span>
                                      <strong>{remainingAnswerGroupCount} 組</strong>
                                    </div>
                                  </div>
                                )}
                              </section>
                            );
                          })}
                        </div>
                        {pendingServiceTasks.length > 0 && (
                          <div className="tl-customer-form-submit-row tl-customer-service-submit-row">
                            {serviceErrorRows.length > 0 && (
                              <div className="tl-customer-form-error-summary" role="alert">
                                <strong>請先修正 {serviceErrorRows.length} 個欄位</strong>
                                <ul>
                                  {serviceErrorRows.slice(0, 6).map((row) => (
                                    <li key={row.key}>
                                      {row.productName} / {row.questionLabel}：{row.error}
                                    </li>
                                  ))}
                                </ul>
                                {serviceErrorRows.length > 6 && (
                                  <span>還有 {serviceErrorRows.length - 6} 個欄位，請依紅框提示修正。</span>
                                )}
                              </div>
                            )}
                            <div className="tl-customer-submit-status">
                              <strong>{answerProgress.incomplete > 0 ? `還有 ${answerProgress.incomplete} 組未完成` : '資料已填寫完成'}</strong>
                              <small>{pendingServiceGroups.length} 項商品 · {pendingServiceTasks.length} 份資料</small>
                            </div>
                            <button
                              className="confirm-button ready"
                              onClick={() => submitServiceTasks(group)}
                              disabled={isSubmitting}
                            >
                              {isSubmitting
                                ? '送出中...'
                                : `送出全部資料（${pendingServiceGroups.length} 項商品／${pendingServiceTasks.length} 份）`}
                            </button>
                          </div>
                        )}
                      </>
                    );
                  })()}
                </div>
              );
            })}
          </div>
        </section>
      )}

      <a
        className="messenger-chat-button"
        href={CUSTOMER_SUPPORT_URL}
        target="_blank"
        rel="noopener noreferrer"
        aria-label="使用 LINE 聯繫客服"
      >
        <div className="messenger-icon">
          <img
            src={LINE_ICON_URL}
            alt="LINE"
            width="32"
            height="32"
          />
        </div>
        <div className="messenger-tooltip">聯繫客服</div>
      </a>

      {notice && (
        <CustomerNoticeModal
          notice={notice}
          onClose={() => setNotice(null)}
        />
      )}
    </div>
  );
}

function CustomerNoticeModal({ notice, onClose }) {
  const messageLines = String(notice.message || '').split('\n').filter(Boolean);
  const detailLines = String(notice.detail || '').split('\n').filter(Boolean);
  const handlePrimaryAction = () => {
    if (typeof notice.onAction === 'function') {
      notice.onAction();
      return;
    }
    onClose();
  };
  const handleSecondaryAction = () => {
    if (typeof notice.onSecondaryAction === 'function') {
      notice.onSecondaryAction();
      return;
    }
    onClose();
  };

  return (
    <div className="tl-customer-notice-overlay" role="presentation" onClick={onClose}>
      <div
        className={`tl-customer-notice-modal ${notice.tone || 'info'}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby="tl-customer-notice-title"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="tl-customer-notice-mark" aria-hidden="true">
          {notice.tone === 'success' ? '✓' : notice.tone === 'error' ? '!' : notice.tone === 'warning' ? '!' : 'i'}
        </div>
        <div className="tl-customer-notice-content">
          <h2 id="tl-customer-notice-title">{notice.title}</h2>
          {messageLines.map((line) => (
            <p key={line}>{line}</p>
          ))}
          {detailLines.length > 0 && (
            <div className="tl-customer-notice-detail">
              {detailLines.map((line) => (
                <span key={line}>{line}</span>
              ))}
            </div>
          )}
        </div>
        <div className="tl-customer-notice-actions">
          {notice.secondaryActionLabel && (
            <button type="button" className="tl-customer-notice-button secondary" onClick={handleSecondaryAction}>
              {notice.secondaryActionLabel}
            </button>
          )}
          <button type="button" className="tl-customer-notice-button" onClick={handlePrimaryAction}>
            {notice.actionLabel || '我知道了'}
          </button>
        </div>
      </div>
    </div>
  );
}

function JamboExchangeItem({ item, index, selectedExchange, onExchangeSelect, onCancelExchange, isDisabled = false }) {
  const [moreOpen, setMoreOpen] = useState(false);
  const displayText = getDisplayText(item.originalProduct?.productType);
  const itemStatusText = getCustomerStatusText(item);
  const sortedExchanges = [...(item.availableExchanges || [])].sort((a, b) => {
    const getProductStockStatus = (product) => {
      return getStockInfo(product, isNoStyleProduct(product)).available ? 1 : 0;
    };
    const statusA = getProductStockStatus(a);
    const statusB = getProductStockStatus(b);
    if (statusA !== statusB) return statusB - statusA;
    return String(a.name || '').localeCompare(String(b.name || ''), 'zh-Hant');
  });

  return (
    <div className={`exchange-item-card ${selectedExchange ? 'selected' : ''}`}>
      <div className="exchange-item-header">
        <div className="item-badge">
          <span className="item-number">第 {index + 1} 項</span>
        </div>
        <div className="item-status">
          {itemStatusText ? (
            <span className="status-confirmed">{itemStatusText}</span>
          ) : selectedExchange?.action === 'cancel' ? (
            <span className="status-cancel">取消</span>
          ) : selectedExchange ? (
            <span className="status-confirmed">已確認</span>
          ) : (
            <span className="status-pending">未選擇</span>
          )}
        </div>
      </div>
      <div className={`tl-customer-choice-summary ${selectedExchange ? 'selected' : ''}`}>
        <span>{selectedExchange ? '目前選擇' : '待選擇'}</span>
        <strong>{getSelectionSummary(selectedExchange)}</strong>
      </div>

      <div className="exchange-flow">
        <div className="original-product">
          <h4>{displayText.originalItemLabel}</h4>
          <div className="product-info">
            <div className="product-image-container">
              {item.originalProduct?.image ? (
                <img
                  src={item.originalProduct.image}
                  alt={item.originalProduct?.name || '缺貨商品'}
                  className="product-image"
                />
              ) : (
                <div className="no-image-placeholder">
                  <span>無圖</span>
                </div>
              )}
            </div>
            <div className="product-details">
              <div className="detail-row">
                <span className="detail-label">料號：</span>
                <span className="detail-value">{item.originalProduct?.itemNumber || '-'}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">品名：</span>
                <span className="detail-value">{item.originalProduct?.name || '-'}</span>
              </div>
              {isDisplayableStyle(item.originalStyle) && (
                <div className="detail-row">
                  <span className="detail-label">樣式：</span>
                  <span className="detail-value">{item.originalStyle}</span>
                </div>
              )}
              <div className="detail-row">
                <span className="detail-label">數量：</span>
                <span className="detail-value highlight">{item.quantity || 1} 件</span>
              </div>
            </div>
          </div>
        </div>

        <div className="exchange-arrow">
          <span className="arrow-icon">→</span>
          <span className="arrow-text">{displayText.arrowText}</span>
        </div>

        <div className="exchange-product">
          <h4>{displayText.exchangeItemLabel}</h4>
          <div className="exchange-options">
            <label className="select-label">請選擇{displayText.actionLabel}</label>
            {item.refundPriceDifference === true && item.exchangeDifferenceRefundAmountSet === true && (
              <div className="tl-customer-difference-refund-note">
                本次換貨完成後將退還差額紅利 {Math.max(0, Number(item.exchangeDifferenceRefundAmountInput || 0) || 0)} 點
              </div>
            )}
            {sortedExchanges.length === 0 && (
              <div className="cancel-notice">
                {item.originalProduct?.allowCancel === true
                  ? '目前這項商品尚未設定可換貨商品，請選擇「不換貨」讓客服協助處理。'
                  : '目前這項商品尚未設定可換貨商品，請聯繫客服協助處理。'}
              </div>
            )}
            <div className="product-selection-grid">
              {sortedExchanges.map((product) => {
                const productKey = getProductKey(product);
                const isProductSelected = selectedExchange?.productId === productKey;
                return (
                  <JamboExchangeProductOption
                    key={productKey}
                    product={product}
                    isSelected={isProductSelected}
                    selectedStyle={isProductSelected ? (selectedExchange?.style || '') : ''}
                    onSelect={() => onExchangeSelect(item, product, selectionStyleForProductCard(product))}
                    onStyleSelect={(style) => onExchangeSelect(item, product, style)}
                    isDisabled={isDisabled}
                  />
                );
              })}
            </div>
            {item.originalProduct?.allowCancel === true && (
              <div className={`tl-more-exchange-options ${selectedExchange?.action === 'cancel' ? 'selected' : ''}`}>
                <button
                  type="button"
                  className="tl-more-options-toggle"
                  onClick={() => setMoreOpen((prev) => !prev)}
                  disabled={isDisabled}
                >
                  <span>更多選項</span>
                  <strong>{moreOpen ? '收合' : '展開'}</strong>
                </button>
                {moreOpen && (
                  <>
                    <button
                      type="button"
                      className={`tl-cancel-refund-row ${selectedExchange?.action === 'cancel' ? 'selected' : ''}`}
                      onClick={() => onCancelExchange(item)}
                      disabled={isDisabled}
                    >
                      <span>不換貨，申請退款</span>
                      {selectedExchange?.action === 'cancel' && (
                        <strong className="tl-cancel-selected-mark" aria-label="已選擇">
                          ✓
                        </strong>
                      )}
                    </button>
                    <p className="tl-refund-note">
                      *退款預設將以紅利點數方式返還。紅利點數無使用期限，1點=1元，下次消費可於結帳時選擇折抵使用。退款金額將依實際付款金額計算，並扣除優惠券折抵、購物金、多件優惠等相關折扣後，依實際可退金額辦理退款。若需改以現金退款，請私訊客服協助處理，現金退款將透過銀行轉帳方式辦理。
                    </p>
                  </>
                )}
              </div>
            )}
            {selectedExchange?.action === 'cancel' && (
              <div className="cancel-notice">
                此商品將不換貨，後續會協助您申請退款
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function getCustomerStatusText(item) {
  if (item.status === 'failed' || item.jamboExchangeStatus === 'failed' || item.jobStatus === 'failed') return '處理中';
  if (item.status === 'exchanged' || item.jamboExchangeStatus === 'success' || item.jobStatus === 'success') return '已換貨';
  if (item.status === 'handled' || item.serviceStatus === 'handled') return '已處理';
  if (item.status === 'filled' || item.customerSelection?.action === 'exchange') return '已送出';
  if (item.status === 'refund_requested' || item.status === 'out_of_stock' || item.customerSelection?.action === 'cancel') return '已送出';
  return '';
}

function defaultStyleForProduct(product) {
  const availableStyles = availableStyleChoicesForProduct(product);
  if (availableStyles.length === 1) return availableStyles[0];
  const styles = styleChoicesForProduct(product);
  return styles.length === 1 ? styles[0] : '';
}

function selectionStyleForProductCard(product) {
  return productNeedsStyleChoice(product) ? '' : defaultStyleForProduct(product);
}

function JamboExchangeProductOption({ product, isSelected, selectedStyle, onSelect, onStyleSelect, isDisabled = false }) {
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const noStyleProduct = isNoStyleProduct(product);
  const stockInfo = getStockInfo(product, noStyleProduct);
  const availableStyles = styleChoicesForProduct(product).filter((style) => stockInfo.availableStyles?.includes(style));
  const needsStyleChoice = availableStyles.length > 1;
  const isMissingStyle = isSelected && needsStyleChoice && !selectedStyle;
  const openPreview = (event) => {
    event?.stopPropagation();
    if (product.image) setIsPreviewOpen(true);
  };

  return (
    <>
      <div
        className={`exchange-product-option ${isSelected ? 'selected' : ''} ${isMissingStyle ? 'needs-style' : ''} ${!stockInfo.available ? 'sold-out' : ''} ${isDisabled ? 'is-disabled' : ''}`}
        role="button"
        tabIndex={stockInfo.available && !isDisabled ? 0 : -1}
        aria-pressed={isSelected}
        aria-disabled={isDisabled || !stockInfo.available}
        onClick={stockInfo.available && !isDisabled ? onSelect : undefined}
        onKeyDown={(event) => {
          if (!stockInfo.available || isDisabled) return;
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            onSelect();
          }
        }}
      >
        <div
          className={`option-image-container ${product.image ? 'can-preview' : ''}`}
        >
          {product.image ? (
            <img
              src={product.image}
              alt={product.name}
              className="option-product-image"
            />
          ) : (
            <div className="option-product-no-image">
              無圖片
            </div>
          )}
          {!stockInfo.available && (
            <div className="sold-out-overlay">
              <span>已額滿</span>
            </div>
          )}
        </div>
        {product.image && (
          <button
            type="button"
            className="tl-image-zoom-button"
            onClick={openPreview}
            disabled={isDisabled}
          >
            放大圖片
          </button>
        )}

        <div className="option-product-info">
          <h5>{product.name}</h5>
          <p>{product.itemNumber || '-'}</p>

          {!noStyleProduct && availableStyles.length > 1 && (
            <div className="style-selection-inline tl-customer-style-select" onClick={(e) => e.stopPropagation()}>
              <select
                className={isMissingStyle ? 'is-required' : ''}
                value={selectedStyle}
                onChange={(e) => onStyleSelect(e.target.value)}
                disabled={!stockInfo.available || isDisabled}
              >
                <option value="">請選擇樣式</option>
                {availableStyles.map((style) => (
                  <option key={style} value={style}>
                    {style}
                  </option>
                ))}
              </select>
              {isMissingStyle && (
                <div className="tl-style-required-hint">請先選擇樣式</div>
              )}
            </div>
          )}

          {!noStyleProduct && availableStyles.length === 1 && (
            <div className="single-style-display">
              樣式：{availableStyles[0]}
            </div>
          )}

          {stockInfo.text && (
            <div className={`stock-status ${stockInfo.status}`}>
              {stockInfo.text}
              {stockInfo.status === 'low' && (
                <div className="low-stock-warning">數量有限</div>
              )}
            </div>
          )}
        </div>

        {isSelected && stockInfo.available && (
          <div className="selected-indicator">✓</div>
        )}
      </div>

      {isPreviewOpen && (
        <div className="tl-image-preview-overlay" role="dialog" aria-modal="true" onClick={() => setIsPreviewOpen(false)}>
          <div className="tl-image-preview-dialog" onClick={(event) => event.stopPropagation()}>
            <button type="button" className="tl-image-preview-close" onClick={() => setIsPreviewOpen(false)}>
              關閉
            </button>
            <img src={product.image} alt={product.name} />
            <div>{product.name}</div>
          </div>
        </div>
      )}
    </>
  );
}

function getStockInfo(product, isNoStyleProduct) {
  if (
    (!product.stockData || Object.keys(product.stockData).length === 0) &&
    (!product.manualStockByStyle || Object.keys(product.manualStockByStyle).length === 0) &&
    (!product.stockSettings || Object.keys(product.stockSettings).length === 0)
  ) {
    return { available: false, status: 'out', text: '已額滿', availableStyles: [] };
  }

  let availableStyles = [];
  let totalStock = 0;

  if (isNoStyleProduct) {
    const stockInfo = stockInfoForStyle(product, '') || product.stockData?.noStyle || product.stockData?.__no_style__ || product.stockData?.[''] || product.stockData?.[Object.keys(product.stockData || {})[0]];
    if (stockInfoIsAvailable(stockInfo) && stockInfoQuantity(stockInfo) >= 999999) {
      availableStyles.push('');
      totalStock = 999999;
    } else {
      const remainingQuantity = stockInfoQuantity(stockInfo);
      if (remainingQuantity > 0) {
        availableStyles.push('');
        totalStock = remainingQuantity;
      }
    }
  } else {
    product.styles?.forEach((style) => {
      const stockInfo = stockInfoForStyle(product, style);
      if (stockInfoIsAvailable(stockInfo) && stockInfoQuantity(stockInfo) >= 999999) {
        availableStyles.push(style);
        totalStock += 999999;
        return;
      }
      const remainingQuantity = stockInfoQuantity(stockInfo);
      if (remainingQuantity > 0) {
        availableStyles.push(style);
        totalStock += remainingQuantity;
      }
    });
  }

  if (availableStyles.length === 0) return { available: false, status: 'out', text: '已額滿', availableStyles: [] };
  if (totalStock > 999990) return { available: true, status: 'unlimited', text: '', availableStyles };
  if (totalStock > 3) return { available: true, status: 'sufficient', text: '', availableStyles };
  return { available: true, status: 'low', text: '', availableStyles };
}

export default TianlanJamboCustomerQuery;
